import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { Box, Card, CardContent, Typography, IconButton, Stack, Chip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PreviewComponentProps } from '@/features/csv-upload';

import { CreateItemTemplateDto } from '../api';

import { ItemForm } from './item-form';

export const ItemTemplateCsvPreview = ({
  results,
}: PreviewComponentProps<CreateItemTemplateDto>) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentResult = results[currentIndex];
  const totalResults = results.length;

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(totalResults - 1, prev + 1));
  };

  if (results.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          {t('csvImport.previewStep.noResults', 'No results to preview')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Navigation Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">{t('csvImport.previewStep.title', 'Preview Results')}</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={t('csvImport.previewStep.resultCount', {
              current: currentIndex + 1,
              total: totalResults,
              defaultValue: `${currentIndex + 1} of ${totalResults}`,
            })}
            color="primary"
            variant="outlined"
          />
          <Box>
            <IconButton
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              size="small"
              aria-label={t('csvImport.previewStep.previous', 'Previous')}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              disabled={currentIndex === totalResults - 1}
              size="small"
              aria-label={t('csvImport.previewStep.next', 'Next')}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Stack>
      </Box>

      {/* Current Result Display */}
      <Card>
        <CardContent>
          <ItemForm template={currentResult} mode="previewMode" />
        </CardContent>
      </Card>
    </Stack>
  );
};
